import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { ArticleInput, ArticleNumber, CustomerInput, ErrorCode, ImportPlan, LegacyArticle, LegacyWorkOrderExtra, RequiredFieldChoices, Role, Row, VehicleInput, WorkOrderInput, WorkOrderLineInput, WorkOrderStatus } from "@szerviz/core";
import { TERMS_DOCUMENTS, DomainError, ERROR_CODES, cleanFeatures, isPlan, MIN_PASSWORD_LENGTH, buildLine, partSearchKey, validateArticle, cleanRequiredChoices, planLegacyWorkOrders, plateSearchKey, type Plan, cleanChannels, todayIso, type ReminderKind } from "@szerviz/core";
import type { MfaEnrollment, MfaRequirement, MfaStatus, ExportBundle, Article, ArticleNumberRow, NewStockMovement, StockMovement, Customer, DataStore, ImportProgress, ImportResult, ItemSuggestion, Member, NewPhoto, Photo, UserPrefs, Vehicle, WorkOrder, WorkOrderDetail, WorkOrderEvent, WorkOrderImportResult, WorkOrderItem, Workshop, PublicQuote, QuoteNotice, QuoteRequest, QuoteChannel, QuoteMessage, SentMessage, SmsUsage, BillingStatus, OpBilling, OpBillingPatch, OpBulkAction, OpMember, OpLogEntry, OpWorkshop, AiCredit, AiPriceHints, AiSettings, OpAiUsage, OpAiToday, OpAiLimit, VehicleFact, VehiclePhotoResult, VehicleTypeNode, VehicleTypeSuggestion, VehicleTypeMatch, ServiceHistoryOrder, OpActivity, ContractDetails, OpContract, Invoice, CancelledInvoice, PaymentMethod, Recommendation, OpStatsOverview, OpStatsWorkshop, OpStatsMonth, OpStatTotals, OpStatFigures, CompanyCandidate, TaxpayerData, UpcomingReminder, SentReminder, FeedbackInput, FeedbackReport, FeedbackVerdict, MyCustomRequests, NewCustomRequest, OpCustomRequest, CustomRequest, CustomRequestStatus, OpTrialActivity } from "./types";
import { OP_STAT_FIGURES, OP_STAT_MONTH, OP_STAT_PLATFORM, type MySubscription, type OpSubscription, type OpSubscriptionPatch, type SubscriptionPatch } from "./types";
import { parseLockedPrices, parseMySubscription } from "./subscription";
import { parsePricing, type Pricing, cleanLogo, logoPath, newLogoVersion, type WorkshopLogo } from "@szerviz/core";
import type { PreparedLogo, IntakeLink, IntakeObjection, IntakeObjectionNotice, NewSignature, PublicIntake, WorkOrderSignature } from "./types";
import { isIntakeView, type SignatureKind, type SignedContent } from "@szerviz/core";

type DbRow = Record<string, unknown>;
const snake = (k: string) => k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
/** Numbers of a statistics row by their camelCase names (the database sends bigint as text). */
const nums = <K extends string>(r: DbRow, keys: readonly K[]) => Object.fromEntries(keys.map((k) => [k, Number(r[snake(k)] ?? 0)])) as Record<K, number>;

const BATCH = 500;

/** The operator's extra figures of one workshop (op_workshop_extras). */
const opExtras = (x: DbRow | undefined) => ({
  discountPct: x?.discount_pct === null || x?.discount_pct === undefined ? null : Number(x.discount_pct),
  aiAllowance: x?.ai_allowance === null || x?.ai_allowance === undefined ? null : Number(x.ai_allowance),
  tags: (x?.tags as string[] | undefined) ?? [],
  featureGrants: (x?.feature_grants as string[] | undefined) ?? [],
  giftMonthHuf: Number(x?.gift_month ?? 0),
  giftTotalHuf: Number(x?.gift_total ?? 0),
  lastSignIn: (x?.last_sign_in as string | undefined) ?? null,
});

const customerToRow = (workshopId: string, c: CustomerInput): DbRow => ({
  workshop_id: workshopId,
  legacy_id: c.legacyId ?? null,
  name: c.name,
  is_company: c.isCompany ?? false,
  tax_number: c.taxNumber ?? null,
  postal_code: c.postalCode ?? null,
  city: c.city ?? null,
  address_line: c.addressLine ?? null,
  phone: c.phone ?? null,
  mobile: c.mobile ?? null,
  email: c.email ?? null,
  contact_person: c.contactPerson ?? null,
  contact_phone: c.contactPhone ?? null,
  notes: c.notes ?? null,
  // only when the form has it, so other saves (quick customer, import) keep the consent
  ...(c.reminderChannels !== undefined ? { reminder_channels: c.reminderChannels } : {}),
});

const rowToCustomer = (r: DbRow): Customer => ({
  id: r.id as string,
  workshopId: r.workshop_id as string,
  createdAt: r.created_at as string,
  legacyId: (r.legacy_id as string) ?? null,
  name: r.name as string,
  isCompany: r.is_company as boolean,
  taxNumber: (r.tax_number as string) ?? null,
  postalCode: (r.postal_code as string) ?? null,
  city: (r.city as string) ?? null,
  addressLine: (r.address_line as string) ?? null,
  phone: (r.phone as string) ?? null,
  mobile: (r.mobile as string) ?? null,
  email: (r.email as string) ?? null,
  contactPerson: (r.contact_person as string) ?? null,
  contactPhone: (r.contact_phone as string) ?? null,
  notes: (r.notes as string) ?? null,
  reminderChannels: cleanChannels(r.reminder_channels),
  reminderConsentAt: (r.reminder_consent_at as string) ?? null,
});

const vehicleToRow = (workshopId: string, v: VehicleInput): DbRow => ({
  workshop_id: workshopId,
  legacy_id: v.legacyId ?? null,
  customer_id: v.customerId ?? null,
  plate_number: v.plateNumber,
  plate_country: v.plateCountry ?? null,
  vin: v.vin ?? null,
  make: v.make ?? null,
  model: v.model ?? null,
  type_description: v.typeDescription ?? null,
  engine_code: v.engineCode ?? null,
  power_kw: v.powerKw ?? null,
  model_year: v.modelYear ?? null,
  fuel_type: v.fuelType ?? null,
  tyre_size: v.tyreSize ?? null,
  odometer_km: v.odometerKm ?? null,
  notes: v.notes ?? null,
  ...vehicleDatesToRow(v),
});

/** The reminder dates, only those the input has (a form without them keeps the stored ones). */
const vehicleDatesToRow = (v: Partial<VehicleInput>): DbRow => ({
  ...("inspectionValidUntil" in v ? { inspection_valid_until: v.inspectionValidUntil ?? null } : {}),
  ...("nextServiceDate" in v ? { next_service_date: v.nextServiceDate ?? null } : {}),
  ...("nextServiceKm" in v ? { next_service_km: v.nextServiceKm ?? null } : {}),
});

const rowToVehicle = (r: DbRow): Vehicle => ({
  id: r.id as string,
  workshopId: r.workshop_id as string,
  createdAt: r.created_at as string,
  legacyId: (r.legacy_id as string) ?? null,
  customerId: (r.customer_id as string) ?? null,
  customerName: ((r.customers as DbRow | null)?.name as string) ?? null,
  typeId: (r.type_id as string) ?? null,
  plateNumber: r.plate_number as string,
  plateCountry: (r.plate_country as string) ?? null,
  vin: (r.vin as string) ?? null,
  make: (r.make as string) ?? null,
  model: (r.model as string) ?? null,
  typeDescription: (r.type_description as string) ?? null,
  engineCode: (r.engine_code as string) ?? null,
  powerKw: (r.power_kw as number) ?? null,
  modelYear: (r.model_year as number) ?? null,
  fuelType: (r.fuel_type as string) ?? null,
  tyreSize: (r.tyre_size as string) ?? null,
  odometerKm: (r.odometer_km as number) ?? null,
  notes: (r.notes as string) ?? null,
  inspectionValidUntil: (r.inspection_valid_until as string) ?? null,
  nextServiceDate: (r.next_service_date as string) ?? null,
  nextServiceKm: (r.next_service_km as number) ?? null,
});

/** Characters that would break a PostgREST filter expression are removed from search text. */
const safe = (q: string) => q.replace(/[,()%*\\]/g, " ").trim();

/** Database errors that carry one of our error codes become translated domain errors. */
function toError(message: string): Error {
  const code = ERROR_CODES.find((c) => message.includes(c));
  return code ? new DomainError(code as ErrorCode, message) : new Error(message);
}

function check<T>(res: { data: T; error: { message: string; code?: string } | null }): T {
  if (res.error) throw toError(res.error.message);
  return res.data;
}

/** A delete refused by a foreign key (23503) becomes the given domain error. */
function checkDelete(res: { error: { message: string; code?: string } | null }, code: ErrorCode): void {
  if (res.error?.code === "23503") throw new DomainError(code);
  check({ data: null, ...res });
}

const articleToRow = (a: ArticleInput): DbRow => ({
  part_number: a.partNumber ?? null,
  name: a.name,
  brand: a.brand ?? null,
  category: a.category ?? null,
  unit: a.unit ?? "db",
  purchase_price: a.purchasePrice ?? null,
  sale_price: a.salePrice ?? null,
  sale_price_mode: a.salePriceMode ?? "gross",
  vat_rate: a.vatRate ?? 27,
  track_stock: a.trackStock ?? false,
  bulk: a.bulk ?? false,
  min_stock: a.minStock ?? null,
  active: a.active ?? true,
  notes: a.notes ?? null,
});

const rowToArticle = (r: DbRow): Article => ({
  id: r.id as string,
  workshopId: r.workshop_id as string,
  partNumber: (r.part_number as string) ?? null,
  name: r.name as string,
  brand: (r.brand as string) ?? null,
  category: (r.category as string) ?? null,
  unit: (r.unit as string) ?? "db",
  purchasePrice: r.purchase_price === null || r.purchase_price === undefined ? null : Number(r.purchase_price),
  salePrice: r.sale_price === null || r.sale_price === undefined ? null : Number(r.sale_price),
  salePriceMode: r.sale_price_mode as Article["salePriceMode"],
  vatRate: Number(r.vat_rate),
  trackStock: !!r.track_stock,
  bulk: !!r.bulk,
  minStock: r.min_stock === null || r.min_stock === undefined ? null : Number(r.min_stock),
  stock: Number(r.stock ?? 0),
  usageCount: Number(r.usage_count ?? 0),
  lastUsed: (r.last_used as string) ?? null,
  active: r.active !== false,
  notes: (r.notes as string) ?? null,
  legacyId: (r.legacy_id as string) ?? null,
  numbers: Array.isArray(r.article_numbers)
    ? (r.article_numbers as DbRow[]).map((n) => ({ id: n.id as string, number: n.number as string, kind: n.kind as ArticleNumberRow["kind"], brand: (n.brand as string) ?? null }))
    : undefined,
});

const PHOTO_BUCKET = "photos";
const LOGO_BUCKET = "logos";
const SIGNATURE_BUCKET = "signatures";
/** Workshop tables in the full data export (each has workshop_id). */
const EXPORT_TABLES = [
  "workshop_members", "customers", "vehicles", "vehicle_external_refs", "work_orders", "work_order_items", "work_order_events",
  "quote_requests", "articles", "article_numbers", "stock_movements", "photos", "work_order_signatures", "intake_links", "intake_objections", "terms_acceptances", "invoices", "subscriber_contracts", "sms_messages", "ai_usage", "ai_credit_purchases", "declined_item_followups", "customer_notices", "reminder_log",
] as const;
/** Tables without an id column are paged by their key. */
const EXPORT_ORDER: Partial<Record<string, string>> = { workshop_members: "user_id", vehicle_external_refs: "vehicle_id" };

const rowToPhoto = (r: DbRow, url: string): Photo => ({
  id: r.id as string,
  workshopId: r.workshop_id as string,
  vehicleId: (r.vehicle_id as string) ?? null,
  workOrderId: (r.work_order_id as string) ?? null,
  kind: r.kind as Photo["kind"],
  url,
  width: (r.width as number) ?? null,
  height: (r.height as number) ?? null,
  caption: (r.caption as string) ?? null,
  takenBy: (r.taken_by as string) ?? null,
  createdAt: r.created_at as string,
  intakeView: isIntakeView(r.intake_view) ? r.intake_view : null,
});

const WORK_ORDER_SELECT = "*, vehicles(plate_number, plate_country, make, model), customers(name), work_order_items(gross, approval), intake_objections(resolved_at)";

const rowToWorkOrder = (r: DbRow): WorkOrder => {
  const v = r.vehicles as DbRow | null;
  const items = (r.work_order_items as DbRow[] | null) ?? [];
  return {
    id: r.id as string,
    workshopId: r.workshop_id as string,
    number: r.number as string,
    status: r.status as WorkOrderStatus,
    vehicleId: r.vehicle_id as string,
    customerId: r.customer_id as string,
    odometerKm: (r.odometer_km as number) ?? null,
  expectedReadyAt: (r.expected_ready_at as string) ?? null,
    customerRequest: (r.customer_request as string) ?? null,
    findings: (r.findings as string) ?? null,
    notes: (r.notes as string) ?? null,
    receivedAt: r.received_at as string,
    readyAt: (r.ready_at as string) ?? null,
    closedAt: (r.closed_at as string) ?? null,
    paymentMethod: (r.payment_method as PaymentMethod) ?? null,
    paidAt: (r.paid_at as string) ?? null,
    needsReview: (r.needs_review as string) ?? null,
    legacyId: (r.legacy_id as string) ?? null,
    updatedAt: (r.updated_at as string) ?? undefined,
    plateNumber: (v?.plate_number as string) ?? "",
    plateCountry: (v?.plate_country as string) ?? null,
    vehicleLabel: v ? [v.make, v.model].filter(Boolean).join(" ") || null : null,
    customerName: ((r.customers as DbRow | null)?.name as string) ?? null,
    gross: items.filter((i) => i.approval !== "declined").reduce((a, i) => a + Number(i.gross), 0),
    itemCount: items.length,
    pendingApprovals: items.filter((i) => i.approval === "pending").length,
    openObjections: ((r.intake_objections as DbRow[] | null) ?? []).filter((x) => !x.resolved_at).length,
  };
};

const rowToItem = (r: DbRow): WorkOrderItem => ({
  id: r.id as string,
  workOrderId: r.work_order_id as string,
  sortOrder: r.sort_order as number,
  kind: r.kind as WorkOrderItem["kind"],
  description: r.description as string,
  partNumber: (r.part_number as string) ?? null,
  articleId: (r.article_id as string) ?? null,
  unit: (r.unit as string) ?? null,
  quantity: Number(r.quantity),
  unitPrice: Number(r.unit_price),
  priceMode: r.price_mode as WorkOrderItem["priceMode"],
  vatRate: Number(r.vat_rate),
  net: Number(r.net),
  vat: Number(r.vat),
  gross: Number(r.gross),
  approval: (r.approval as WorkOrderItem["approval"]) ?? "none",
  optional: r.optional === true,
  customerSupplied: r.customer_supplied === true,
});

const rowToQuote = (r: DbRow): QuoteRequest => ({
  id: r.id as string,
  token: r.token as string,
  channel: r.channel as QuoteRequest["channel"],
  itemIds: (r.item_ids as string[]) ?? [],
  createdAt: r.created_at as string,
  expiresAt: r.expires_at as string,
  answeredAt: (r.answered_at as string) ?? null,
  approvedIds: (r.approved_ids as string[]) ?? [],
  note: (r.note as string) ?? null,
});

const workOrderToRow = (w: WorkOrderInput): DbRow => ({
  vehicle_id: w.vehicleId,
  customer_id: w.customerId,
  odometer_km: w.odometerKm,
  expected_ready_at: w.expectedReadyAt || null,
  customer_request: w.customerRequest?.trim() || null,
  // only sent when the form has the field, so other callers never wipe it
  ...(w.findings !== undefined ? { findings: w.findings?.trim() || null } : {}),
  notes: w.notes?.trim() || null,
});

const PAGE = 1000;

const origin = () => globalThis.location?.origin;

const VEHICLE_SELECT = "*, customers(name)";

/** The cloud backend. Every query runs as the signed in user, so Row Level Security applies. */
export class SupabaseStore implements DataStore {
  readonly mode = "cloud" as const;
  readonly allowSignUp: boolean;
  private sb: SupabaseClient;

  constructor(url: string, publishableKey: string, options: { allowSignUp?: boolean; client?: SupabaseClient } = {}) {
    this.sb = options.client ?? createClient(url, publishableKey);
    this.allowSignUp = options.allowSignUp ?? false;
  }

  async currentUser() {
    const { data } = await this.sb.auth.getUser();
    return data.user ? { id: data.user.id, email: data.user.email ?? "" } : null;
  }
  onAuthChange(listener: () => void) {
    const { data } = this.sb.auth.onAuthStateChange(() => listener());
    return () => data.subscription.unsubscribe();
  }
  async signIn(email: string, password: string) {
    const { error } = await this.sb.auth.signInWithPassword({ email, password });
    // sign up needs a confirmed e-mail address (Supabase Auth "Confirm email")
    if (error && ((error as { code?: string }).code === "email_not_confirmed" || /email not confirmed/i.test(error.message))) throw new DomainError("EMAIL_NOT_CONFIRMED");
    if (error) throw new Error(error.message);
  }
  async signUp(email: string, password: string, language?: string) {
    const { data, error } = await this.sb.auth.signUp({ email, password, options: language ? { data: { language } } : undefined });
    if (error) throw new Error(error.message);
    return { needsConfirmation: !data.session };
  }
  async signOut() {
    await this.sb.auth.signOut();
  }
  async requestPasswordReset(email: string) {
    await this.sb.auth.resetPasswordForEmail(email, { redirectTo: origin() });
  }
  async setPassword(password: string) {
    if (password.length < MIN_PASSWORD_LENGTH) throw new DomainError("PASSWORD_TOO_SHORT");
    const { error } = await this.sb.auth.updateUser({ password });
    if (error) throw toError(error.message);
  }

  // ---- two-step sign in (Supabase Auth MFA, TOTP) ----
  async mfaStatus(): Promise<MfaStatus> {
    const [factors, aal] = await Promise.all([this.sb.auth.mfa.listFactors(), this.sb.auth.mfa.getAuthenticatorAssuranceLevel()]);
    const factor = (factors.data?.totp ?? []).some((f) => f.status === "verified");
    return { factor, verified: aal.data?.currentLevel === "aal2" };
  }
  async mfaRequirement(): Promise<MfaRequirement> {
    const { data, error } = await this.sb.rpc("mfa_requirement");
    // a database without the function yet: nothing is required
    const r = (error ? null : ((data as DbRow[] | null)?.[0] ?? null)) as DbRow | null;
    const reason = r?.reason;
    return {
      required: r?.required === true,
      reason: reason === "operator" || reason === "owner" || reason === "manager" ? reason : null,
      testExempt: r?.test_exempt === true,
    };
  }
  async mfaEnroll(): Promise<MfaEnrollment> {
    // an unfinished attempt (page closed before the code) would block a new one with the same name
    const { data: list } = await this.sb.auth.mfa.listFactors();
    for (const f of list?.all ?? []) {
      if (f.factor_type === "totp" && f.status === "unverified") await this.sb.auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await this.sb.auth.mfa.enroll({ factorType: "totp", friendlyName: `Garagevo ${new Date().toISOString().slice(0, 16)}`, issuer: "Garagevo" });
    if (error || !data) throw new DomainError("MFA_FAILED", error?.message);
    return { factorId: data.id, secret: data.totp.secret, uri: data.totp.uri };
  }
  async mfaConfirmEnroll(factorId: string, code: string): Promise<string[]> {
    const { error } = await this.sb.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
    if (error) throw new DomainError("MFA_CODE_INVALID", error.message);
    return this.mfaNewBackupCodes();
  }
  async mfaVerify(code: string) {
    const { data } = await this.sb.auth.mfa.listFactors();
    const factor = (data?.totp ?? []).find((f) => f.status === "verified");
    if (!factor) throw new DomainError("MFA_FAILED");
    const { error } = await this.sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code: code.trim() });
    if (error) throw new DomainError("MFA_CODE_INVALID", error.message);
  }
  async mfaRecover(code: string) {
    const { error } = await this.sb.functions.invoke("mfa-recover", { body: { code } });
    if (error) {
      let c = "MFA_FAILED";
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) c = body.error;
      } catch {
        // no JSON body
      }
      throw toError(c);
    }
    // the server signed the user out everywhere when the authenticator was removed
    await this.sb.auth.signOut({ scope: "local" });
  }
  async mfaNewBackupCodes(): Promise<string[]> {
    return check(await this.sb.rpc("mfa_new_backup_codes")) as string[];
  }
  async mfaBackupCodesLeft() {
    const { data, error } = await this.sb.rpc("mfa_backup_codes_left");
    return error ? 0 : Number(data ?? 0);
  }
  async mfaDisable() {
    const { data } = await this.sb.auth.mfa.listFactors();
    for (const f of data?.all ?? []) {
      if (f.factor_type !== "totp") continue;
      const { error } = await this.sb.auth.mfa.unenroll({ factorId: f.id });
      if (error) throw new DomainError("MFA_FAILED", error.message);
    }
    check(await this.sb.rpc("mfa_clear_backup_codes"));
  }
  async opResetMfa(workshopId: string, userId: string) {
    const { data, error } = await this.sb.functions.invoke("mfa-reset", { body: { workshopId, userId } });
    if (error) {
      let c = "MFA_FAILED";
      let detail = "";
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) c = body.error;
        if (body?.detail) detail = body.detail;
      } catch {
        // no JSON body
      }
      const e = toError(c);
      throw e instanceof DomainError ? new DomainError(e.code, detail || undefined) : e;
    }
    return { mailed: (data as { mailed?: boolean } | null)?.mailed === true };
  }

  async listMembers(workshopId: string): Promise<Member[]> {
    const rows = check(await this.sb.rpc("workshop_member_list", { p_workshop_id: workshopId })) as DbRow[];
    return rows.map((r) => ({ userId: r.user_id as string, email: r.email as string, role: r.role as Role }));
  }
  async inviteMember(workshopId: string, email: string, role: Role) {
    const { data, error } = await this.sb.functions.invoke("invite-member", {
      body: { workshopId, email, role, redirectTo: origin() },
    });
    if (error) {
      let code = "INVITE_FAILED";
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) code = body.error;
      } catch {
        // no JSON body
      }
      throw toError(code);
    }
    return (data as { status: "invited" | "added" }).status;
  }
  async sendQuote(workOrderId: string, channel: QuoteChannel, message: QuoteMessage) {
    return this.invokeSend({ workOrderId, channel, ...message });
  }
  async vehicleRecommendations(vehicleId: string): Promise<Recommendation[]> {
    const rows = check(await this.sb.rpc("vehicle_recommendations", { p_vehicle_id: vehicleId })) as DbRow[];
    return rows.map((r) => ({
      itemId: r.item_id as string,
      workOrderId: r.work_order_id as string,
      workOrderNumber: r.work_order_number as string,
      declinedOn: r.declined_on as string,
      gross: Number(r.gross),
      line: {
        kind: r.kind as WorkOrderLineInput["kind"], description: r.description as string, partNumber: (r.part_number as string) ?? null,
        unit: (r.unit as string) ?? null, quantity: Number(r.quantity), unitPrice: Number(r.unit_price),
        priceMode: r.price_mode as WorkOrderLineInput["priceMode"], vatRate: Number(r.vat_rate),
      },
    }));
  }
  async closeRecommendation(itemId: string, workOrderId: string | null) {
    check(await this.sb.rpc("close_recommendation", { p_item_id: itemId, p_work_order_id: workOrderId }));
  }
  async sendDeclinedWork(workOrderId: string, message: { subject: string; text: string }) {
    return this.invokeSend({ workOrderId, channel: "declinedEmail", ...message });
  }
  async declinedWorkSent(workOrderId: string) {
    const r = check(await this.sb.from("customer_notices").select("sent_at").eq("work_order_id", workOrderId).eq("kind", "declinedWork").maybeSingle()) as DbRow | null;
    return r ? (r.sent_at as string) : null;
  }
  async saveVehicleDates(vehicleId: string, dates: Pick<VehicleInput, "inspectionValidUntil" | "nextServiceDate" | "nextServiceKm">) {
    check(await this.sb.from("vehicles").update(vehicleDatesToRow(dates)).eq("id", vehicleId).select("id").single());
  }
  async upcomingReminders(workshopId: string, days = 60): Promise<UpcomingReminder[]> {
    const rows = check(await this.sb.rpc("upcoming_reminders", { p_workshop_id: workshopId, p_days: days, p_today: todayIso() })) as DbRow[];
    return rows.map((r) => ({
      vehicleId: r.vehicle_id as string,
      plateNumber: r.plate_number as string,
      plateCountry: (r.plate_country as string) ?? null,
      vehicleLabel: [r.make, r.model].filter(Boolean).join(" ") || null,
      customerId: (r.customer_id as string) ?? null,
      customerName: (r.customer_name as string) ?? null,
      hasEmail: !!r.has_email,
      hasPhone: !!r.has_phone,
      consent: !!r.consent,
      kind: r.kind as ReminderKind,
      dueDate: r.due_date as string,
      dueKm: (r.due_km as number) ?? null,
      sendFrom: r.send_from as string,
      channels: cleanChannels(r.channels),
      sentChannels: cleanChannels(r.sent_channels),
    }));
  }
  async sentReminders(workshopId: string): Promise<SentReminder[]> {
    const rows = check(await this.sb.rpc("sent_reminders", { p_workshop_id: workshopId, p_limit: 100 })) as DbRow[];
    return rows.map((r) => ({
      id: r.id as string,
      vehicleId: r.vehicle_id as string,
      plateNumber: r.plate_number as string,
      plateCountry: (r.plate_country as string) ?? null,
      kind: r.kind as ReminderKind,
      dueDate: r.due_date as string,
      channel: r.channel === "sms" ? "sms" : "email",
      recipient: (r.recipient as string) ?? null,
      manual: !!r.manual,
      sentAt: r.sent_at as string,
    }));
  }
  async sendReminderNow(vehicleId: string, kind: ReminderKind) {
    const { data, error } = await this.sb.functions.invoke("send-reminders", { body: { vehicleId, kind } });
    if (error) {
      let code = "SEND_FAILED";
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) code = body.error;
      } catch {
        // no JSON body
      }
      throw toError(code);
    }
    const d = data as { sent: { channel: "sms" | "email"; to: string }[]; price: number };
    return { sent: d.sent ?? [], price: d.price ?? 0 };
  }
  async sendTestSms(workshopId: string, to: string, text: string) {
    return this.invokeSend({ workshopId, channel: "smsTest", to, text });
  }
  private async invokeSend(body: Record<string, unknown>): Promise<SentMessage> {
    const { data, error } = await this.sb.functions.invoke("send-quote", { body });
    if (error) {
      let code = "SEND_FAILED";
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) code = body.error;
      } catch {
        // no JSON body
      }
      throw toError(code);
    }
    const d = data as { to: string; price?: number };
    return { to: d.to, price: d.price ?? 0 };
  }
  async smsUsage(workshopId: string): Promise<SmsUsage> {
    const rows = check(await this.sb.rpc("sms_usage", { p_workshop_id: workshopId })) as DbRow[];
    const r = rows[0] ?? {};
    return { count: Number(r.sms_count ?? 0), paidCount: Number(r.paid_count ?? 0), amount: Number(r.amount ?? 0), currency: (r.currency as string) ?? "HUF" };
  }
  async smsPhoneSet(workshopId: string) {
    return check(await this.sb.rpc("sms_phone_set", { p_workshop_id: workshopId })) as boolean;
  }
  async createSmsSetupToken(workshopId: string) {
    return check(await this.sb.rpc("create_sms_setup_token", { p_workshop_id: workshopId })) as string;
  }
  async smsSetupInfo(token: string) {
    return (check(await this.sb.rpc("sms_setup_info", { p_token: token })) as string | null) ?? null;
  }
  async completeSmsSetup(token: string, user: string, password: string) {
    check(await this.sb.rpc("complete_sms_setup", { p_token: token, p_user: user, p_password: password }));
  }
  async myBillingStatus(workshopId: string) {
    return ((check(await this.sb.rpc("my_billing_status", { p_workshop_id: workshopId })) as string | null) ?? "trial") as BillingStatus;
  }
  async mySubscription(workshopId: string): Promise<MySubscription> {
    return parseMySubscription(check(await this.sb.rpc("my_subscription", { p_workshop_id: workshopId })));
  }
  async setSubscription(workshopId: string, patch: SubscriptionPatch) {
    check(await this.sb.rpc("set_subscription", { p_workshop_id: workshopId, p_patch: patch }));
  }
  async myCustomRequests(workshopId: string): Promise<MyCustomRequests> {
    const r = (check(await this.sb.rpc("my_custom_requests", { p_workshop_id: workshopId })) ?? {}) as DbRow;
    const list = Array.isArray(r.requests) ? (r.requests as DbRow[]) : [];
    return {
      year: Number(r.year ?? new Date().getFullYear()), used: Number(r.used ?? 0), included: Number(r.included ?? 0),
      requests: list.map((x) => customRequestOf({ ...x, created_at: x.createdAt })),
    };
  }
  async opSubscriptions(): Promise<OpSubscription[]> {
    const rows = check(await this.sb.rpc("op_subscriptions")) as DbRow[];
    return rows.map((r) => ({
      workshopId: r.workshop_id as string,
      effectivePlan: isPlan(r.effective_plan) ? r.effective_plan : "basic",
      readOnly: r.read_only === true,
      trialUntil: (r.trial_until as string) ?? null,
      planAfterTrial: isPlan(r.plan_after_trial) ? r.plan_after_trial : null,
      period: r.billing_period === "yearly" ? "yearly" : "monthly",
      extraSeats: Number(r.extra_seats ?? 0),
      billingAnchor: (r.billing_anchor as string) ?? null,
      founder: r.founder === true,
      founderUntil: (r.founder_until as string) ?? null,
      priceLockUntil: (r.price_lock_until as string) ?? null,
      lockedPrices: parseLockedPrices(r.locked_prices),
    }));
  }
  async opUpdateSubscription(workshopId: string, patch: OpSubscriptionPatch) {
    check(await this.sb.rpc("op_update_subscription", { p_workshop_id: workshopId, p_patch: patch }));
  }
  async opPricing(): Promise<Pricing> {
    const p = parsePricing(check(await this.sb.rpc("op_pricing")));
    if (!p) throw new DomainError("NOT_ALLOWED");
    return p;
  }
  async opSetPricing(pricing: Pricing) {
    check(await this.sb.rpc("op_set_pricing", { p_value: pricing }));
  }
  /** The plan that works now in each workshop (Profi in the first week of the trial); the stored plan when it cannot be read (an older database, or before the second step). */
  private async effectivePlans(ids: string[]): Promise<Map<string, Plan>> {
    const out = new Map<string, Plan>();
    for (const id of ids) {
      const { data, error } = await this.sb.rpc("my_subscription", { p_workshop_id: id });
      const p = !error && data && typeof data === "object" ? (data as DbRow).effectivePlan : null;
      if (isPlan(p)) out.set(id, p);
    }
    return out;
  }
  async isPlatformAdmin() {
    const { data, error } = await this.sb.rpc("is_platform_admin");
    return !error && data === true;
  }
  async opWorkshops(): Promise<OpWorkshop[]> {
    const rows = check(await this.sb.rpc("op_workshops")) as DbRow[];
    const extras = new Map((check(await this.sb.rpc("op_workshop_extras")) as DbRow[]).map((x) => [x.workshop_id as string, x]));
    return rows.map((r) => ({
      ...opExtras(extras.get(r.id as string)),
      id: r.id as string,
      name: r.name as string,
      plan: r.plan as Plan,
      features: (r.features as string[]) ?? [],
      createdAt: r.created_at as string,
      ownerEmail: (r.owner_email as string) ?? null,
      memberCount: Number(r.member_count ?? 0),
      workOrderCount: Number(r.work_order_count ?? 0),
      lastActivity: (r.last_activity as string) ?? null,
      termsAccepted: r.terms_accepted === true,
      smsCount: Number(r.sms_count ?? 0),
      smsAmount: Number(r.sms_amount ?? 0),
      status: (r.billing_status as BillingStatus) ?? "trial",
      monthlyFee: r.monthly_fee === null || r.monthly_fee === undefined ? null : Number(r.monthly_fee),
      paidUntil: (r.paid_until as string) ?? null,
      note: (r.billing_note as string) ?? null,
    }));
  }
  async opUpdateBilling(workshopId: string, patch: OpBillingPatch) {
    check(await this.sb.rpc("op_update_billing", { p_workshop_id: workshopId, p_patch: patch }));
  }
  async opMemberList(workshopId: string): Promise<OpMember[]> {
    const rows = check(await this.sb.rpc("op_member_list", { p_workshop_id: workshopId })) as DbRow[];
    // who has an authenticator (a database without the function yet: not known)
    const mfa = await this.sb.rpc("op_member_mfa", { p_workshop_id: workshopId });
    const has = new Map(((mfa.error ? [] : mfa.data) as DbRow[]).map((x) => [x.user_id as string, x.has_factor === true]));
    return rows.map((r) => ({
      userId: r.user_id as string, email: r.email as string, role: r.role as Role, createdAt: (r.created_at as string) ?? null, lastSignIn: (r.last_sign_in_at as string) ?? null,
      ...(has.has(r.user_id as string) ? { mfa: has.get(r.user_id as string)! } : {}),
    }));
  }
  async opSetMemberRole(workshopId: string, userId: string, role: Role) {
    check(await this.sb.rpc("op_set_member_role", { p_workshop_id: workshopId, p_user_id: userId, p_role: role }));
  }
  async opRemoveMember(workshopId: string, userId: string) {
    check(await this.sb.rpc("op_remove_member", { p_workshop_id: workshopId, p_user_id: userId }));
  }
  async opSendPasswordReset(workshopId: string, email: string) {
    // the database checks the address belongs to the workshop and logs it; Supabase Auth sends the mail
    check(await this.sb.rpc("op_note_password_reset", { p_workshop_id: workshopId, p_email: email }));
    const { error } = await this.sb.auth.resetPasswordForEmail(email, { redirectTo: origin() });
    if (error) throw new DomainError("SEND_FAILED", error.message);
  }
  async opSetFeatureGrants(workshopId: string, keys: string[]) {
    check(await this.sb.rpc("op_set_feature_grants", { p_workshop_id: workshopId, p_keys: keys }));
  }
  async opBulk(workshopIds: string[], a: OpBulkAction) {
    return check(await this.sb.rpc("op_bulk", { p_workshop_ids: workshopIds, p_action: a.action, p_value: a.value })) as string;
  }
  async opMembers(workshopId: string) {
    const rows = check(await this.sb.rpc("op_members", { p_workshop_id: workshopId })) as DbRow[];
    return rows.map((r) => ({ email: r.email as string, role: r.role as Role }));
  }
  async opSetPlan(workshopId: string, plan: Plan) {
    check(await this.sb.rpc("op_set_plan", { p_workshop_id: workshopId, p_plan: plan }));
  }
  async opSetFeatures(workshopId: string, features: string[]) {
    check(await this.sb.rpc("op_set_features", { p_workshop_id: workshopId, p_features: features }));
  }
  async opSetBilling(workshopId: string, b: OpBilling) {
    check(await this.sb.rpc("op_set_billing", { p_workshop_id: workshopId, p_status: b.status, p_monthly_fee: b.monthlyFee, p_paid_until: b.paidUntil, p_note: b.note }));
  }
  async opLog(workshopId: string): Promise<OpLogEntry[]> {
    const rows = check(await this.sb.rpc("op_log", { p_workshop_id: workshopId })) as DbRow[];
    return rows.map((r) => ({ at: r.at as string, adminEmail: (r.admin_email as string) ?? null, action: r.action as string, details: r.details }));
  }
  async vehicleTypeSuggest(vehicleId: string): Promise<VehicleTypeSuggestion[]> {
    const rows = check(await this.sb.rpc("vehicle_type_suggest", { p_vehicle_id: vehicleId })) as DbRow[];
    return rows.map((r) => ({ id: r.id as string, make: r.make as string, model: r.model as string, engine: r.engine as string, score: Number(r.score ?? 0) }));
  }
  async vehicleTypeChildren(parentId: string | null): Promise<VehicleTypeNode[]> {
    const rows = check(await this.sb.rpc("vehicle_type_children", { p_parent: parentId })) as DbRow[];
    return rows.map((r) => ({ id: r.id as string, level: r.level as VehicleTypeNode["level"], name: r.name as string }));
  }
  async vehicleTypeMatch(vehicleId: string, data: { make?: string | null; model?: string | null; engineCode?: string | null; powerKw?: number | null }): Promise<VehicleTypeMatch[]> {
    const rows = check(await this.sb.rpc("vehicle_type_match", {
      p_vehicle_id: vehicleId, p_make: data.make ?? null, p_model: data.model ?? null, p_engine_code: data.engineCode ?? null, p_power_kw: data.powerKw ?? null,
    })) as DbRow[];
    return rows.map((r) => ({ id: r.id as string, level: r.level as VehicleTypeMatch["level"], make: r.make as string, model: r.model as string, engine: (r.engine as string) ?? null, score: Number(r.score ?? 0) }));
  }
  async serviceHistory(vehicleId: string): Promise<ServiceHistoryOrder[]> {
    const rows = check(await this.sb.from("work_orders").select("id, number, status, closed_at, odometer_km, work_order_items(*)")
      .eq("vehicle_id", vehicleId).eq("status", "closed").order("closed_at", { ascending: false }).limit(100)) as DbRow[];
    return rows.map((r) => ({
      id: r.id as string, number: r.number as string, status: r.status as WorkOrderStatus, closedAt: (r.closed_at as string) ?? null,
      odometerKm: (r.odometer_km as number) ?? null,
      items: ((r.work_order_items as DbRow[] | null) ?? []).map(rowToItem).sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }
  async setVehicleType(vehicleId: string, typeId?: string) {
    return check(await this.sb.rpc("set_vehicle_type", { p_vehicle_id: vehicleId, p_type_id: typeId ?? null })) as string;
  }
  async clearVehicleType(vehicleId: string) {
    check(await this.sb.rpc("clear_vehicle_type", { p_vehicle_id: vehicleId }));
  }
  async vehicleTypePath(typeId: string): Promise<VehicleTypeNode[]> {
    const rows = check(await this.sb.rpc("vehicle_type_path", { p_type_id: typeId })) as DbRow[];
    return rows.map((r) => ({ id: r.id as string, level: r.level as VehicleTypeNode["level"], name: r.name as string }));
  }
  async vehicleDataSheet(vehicleId: string): Promise<VehicleFact[]> {
    const rows = check(await this.sb.rpc("vehicle_data_sheet", { p_vehicle_id: vehicleId })) as DbRow[];
    return rows.map((r) => ({
      id: r.id as string,
      field: r.field as string,
      value: r.value as string,
      sourceUrl: (r.source_url as string) ?? null,
      origin: r.origin as VehicleFact["origin"],
      confirms: Number(r.confirms ?? 0),
      rejects: Number(r.rejects ?? 0),
      percent: r.percent === null || r.percent === undefined ? null : Number(r.percent),
      confirmed: r.confirmed === true,
      myVote: r.my_vote === null || r.my_vote === undefined ? null : r.my_vote === true,
      previewUrl: (r.preview_url as string | undefined) ?? null,
    }));
  }
  async addVehicleFact(vehicleId: string, field: string, value: string, sourceUrl?: string) {
    check(await this.sb.rpc("add_vehicle_fact", { p_vehicle_id: vehicleId, p_field: field, p_value: value, p_source_url: sourceUrl ?? null }));
  }
  async voteVehicleFact(vehicleId: string, factId: string, agrees: boolean | null) {
    check(await this.sb.rpc("vote_vehicle_fact", { p_vehicle_id: vehicleId, p_fact_id: factId, p_agrees: agrees }));
  }
  private aiStatus: Promise<boolean> | null = null;
  async aiAvailable() {
    this.aiStatus ??= this.sb.functions
      .invoke("ai-vehicle", { body: { action: "status" } })
      .then(({ data }) => (data as { available?: boolean } | null)?.available === true)
      .catch(() => false);
    return this.aiStatus;
  }
  private async invokeAi<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.sb.functions.invoke("ai-vehicle", { body });
    if (error) {
      let code = "AI_FAILED";
      let detail = "";
      try {
        const b = await (error as { context?: Response }).context?.json();
        if (b?.error) code = b.error;
        if (typeof b?.detail === "string") detail = b.detail;
      } catch {
        // no JSON body (network error, timeout)
        detail = error.message;
      }
      const e = toError(code);
      throw e instanceof DomainError && detail ? new DomainError(e.code, detail) : e;
    }
    return data as T;
  }
  async readVehiclePhoto(workshopId: string, imageBase64: string, mediaType: string) {
    return (await this.invokeAi<{ result: VehiclePhotoResult }>({ action: "photo", workshopId, image: imageBase64, mediaType })).result;
  }
  async fillDataSheet(vehicleId: string) {
    return (await this.invokeAi<{ added: number }>({ action: "fill", vehicleId })).added;
  }
  async aiPack(vehicleId: string, pack: string) {
    return this.invokeAi<{ facts: { value: string; source: string | null }[]; charged: number; cached: boolean; knownSource?: boolean }>({ action: "pack", vehicleId, pack });
  }
  async packKnownSource(vehicleId: string, pack: string) {
    return check(await this.sb.rpc("pack_known_source", { p_vehicle_id: vehicleId, p_pack: pack })) === true;
  }
  async askVehicleData(vehicleId: string, question: string) {
    return this.invokeAi<{ answer: string; facts: { value: string; source: string | null }[] }>({ action: "ask", vehicleId, question });
  }
  async typeFilled(vehicleId: string) {
    return check(await this.sb.rpc("type_filled", { p_vehicle_id: vehicleId })) as boolean;
  }
  async seats(workshopId: string) {
    const r = ((check(await this.sb.rpc("seats", { p_workshop_id: workshopId })) as DbRow[])[0] ?? {}) as DbRow;
    return { used: Number(r.used ?? 0), limit: Number(r.seat_limit ?? 100000) };
  }
  async opActivity(): Promise<OpActivity[]> {
    const rows = check(await this.sb.rpc("op_activity")) as DbRow[];
    return rows.map((r) => ({
      workshopId: r.workshop_id as string,
      workOrders: Number(r.work_orders ?? 0),
      vehicles: Number(r.vehicles ?? 0),
      members: Number(r.members ?? 0),
      seatLimit: Number(r.seat_limit ?? 100000),
    }));
  }
  async opSetSeats(workshopId: string, maxUsers: number | null) {
    check(await this.sb.rpc("op_set_seats", { p_workshop_id: workshopId, p_max_users: maxUsers }));
  }
  async aiPriceHints(workshopId: string): Promise<AiPriceHints> {
    const rows = check(await this.sb.rpc("ai_price_hints", { p_workshop_id: workshopId })) as DbRow[];
    const by = Object.fromEntries(rows.map((r) => [String(r.kind), Number(r.huf ?? 0)]));
    return { dataPack: by.dataPack ?? 0, dataPackDiagram: by.dataPackDiagram ?? 0, dataPackKnown: by.dataPackKnown ?? 0, dataQuestion: by.dataQuestion ?? 0 };
  }
  async aiCredit(workshopId: string): Promise<AiCredit> {
    const r = ((check(await this.sb.rpc("ai_credit", { p_workshop_id: workshopId })) as DbRow[])[0] ?? {}) as DbRow;
    return {
      allowance: Number(r.allowance ?? 0),
      usedThisMonth: Number(r.used_this_month ?? 0),
      boughtLeft: Number(r.bought_left ?? 0),
      available: Number(r.available ?? 0),
      packs: ((r.packs as number[]) ?? []).map(Number),
    };
  }
  async buyAiCredit(workshopId: string, amount: number) {
    check(await this.sb.rpc("buy_ai_credit", { p_workshop_id: workshopId, p_amount: amount }));
  }
  async opAiUsage(month?: string): Promise<OpAiUsage[]> {
    const rows = check(await this.sb.rpc("op_ai_usage", month ? { p_month: month } : {})) as DbRow[];
    return rows.map((r) => ({
      workshopId: r.workshop_id as string,
      calls: Number(r.calls ?? 0),
      costHuf: Number(r.cost_huf ?? 0),
      chargedHuf: Number(r.charged_huf ?? 0),
      sharedCostHuf: Number(r.shared_cost_huf ?? 0),
      boughtHuf: Number(r.bought_huf ?? 0),
    }));
  }
  async opAiToday(): Promise<OpAiToday> {
    const r = (check(await this.sb.rpc("op_ai_today")) ?? {}) as DbRow;
    return { day: String(r.day ?? ""), costHuf: Number(r.costHuf ?? 0), calls: Number(r.calls ?? 0), capHuf: Number(r.capHuf ?? 0), alertAt: (r.alertAt as string) ?? null };
  }
  async opAiLimits(): Promise<OpAiLimit[]> {
    const rows = check(await this.sb.rpc("op_ai_limits")) as DbRow[];
    const n = (v: unknown) => (v === null || v === undefined ? null : Number(v));
    return rows.map((r) => ({
      workshopId: r.workshop_id as string,
      dailyCapHuf: n(r.daily_cap_huf),
      purchaseLimitHuf: n(r.purchase_limit_huf),
      todayCostHuf: Number(r.today_cost_huf ?? 0),
      monthBoughtHuf: Number(r.month_bought_huf ?? 0),
    }));
  }
  async opAiSettings(): Promise<AiSettings> {
    return check(await this.sb.rpc("op_ai_settings")) as AiSettings;
  }
  async opSetAiSettings(settings: AiSettings) {
    check(await this.sb.rpc("op_set_ai_settings", { p_value: settings }));
  }
  async clearSmsPhone(workshopId: string) {
    check(await this.sb.rpc("clear_sms_phone", { p_workshop_id: workshopId }));
  }
  async updateMemberRole(workshopId: string, userId: string, role: Role) {
    const res = await this.sb.from("workshop_members").update({ role }).eq("workshop_id", workshopId).eq("user_id", userId).select("user_id");
    if ((check(res) as DbRow[]).length === 0) throw new DomainError("NOT_OWNER");
  }
  async removeMember(workshopId: string, userId: string) {
    const res = await this.sb.from("workshop_members").delete().eq("workshop_id", workshopId).eq("user_id", userId).select("user_id");
    if ((check(res) as DbRow[]).length === 0) throw new DomainError("NOT_OWNER");
  }

  async getPrefs(): Promise<UserPrefs> {
    const { data } = await this.sb.auth.getUser();
    const m = (data.user?.user_metadata ?? {}) as Record<string, unknown>;
    return {
      helpEnabled: m.help_enabled !== false,
      helpSince: typeof m.help_since === "string" ? m.help_since : null,
      helpAsked: m.help_asked === true,
    };
  }
  async savePrefs(patch: Partial<UserPrefs>): Promise<UserPrefs> {
    const data: Record<string, unknown> = {};
    if (patch.helpEnabled !== undefined) data.help_enabled = patch.helpEnabled;
    if (patch.helpSince !== undefined) data.help_since = patch.helpSince;
    if (patch.helpAsked !== undefined) data.help_asked = patch.helpAsked;
    const { error } = await this.sb.auth.updateUser({ data });
    if (error) throw toError(error.message);
    return this.getPrefs();
  }

  async myWorkshops(): Promise<Workshop[]> {
    const rows = check(await this.sb.from("workshops").select("id, name, language, currency, plan, settings").order("name")) as DbRow[];
    // features the operator allowed beyond the package (a database without the table yet: none)
    const grants = await this.sb.from("workshop_feature_grants").select("workshop_id, feature_key");
    const extra = (id: string) => ((grants.error ? [] : grants.data) as DbRow[]).filter((g) => g.workshop_id === id).map((g) => g.feature_key as string);
    const effective = await this.effectivePlans(rows.map((r) => r.id as string));
    const planOf = (r: DbRow): Plan => effective.get(r.id as string) ?? (isPlan(r.plan) ? r.plan : "basic");
    return rows.map((r) => ({
      extraFeatures: extra(r.id as string),
      id: r.id as string,
      name: r.name as string,
      language: r.language as string,
      currency: r.currency as string,
      requiredFields: cleanRequiredChoices((r.settings as Record<string, unknown> | null)?.requiredFields),
      general: ((r.settings as Record<string, unknown> | null)?.general as Record<string, number | string | null>) ?? {},
      permissions: ((r.settings as Record<string, unknown> | null)?.permissions as Record<string, Role[]>) ?? {},
      plan: planOf(r),
      features: cleanFeatures(planOf(r), (r.settings as Record<string, unknown> | null)?.features, extra(r.id as string)),
      logo: cleanLogo((r.settings as Record<string, unknown> | null)?.logo, r.id as string),
    }));
  }
  async createWorkshop(name: string, language: string): Promise<Workshop> {
    const id = check(await this.sb.rpc("create_workshop", { p_name: name, p_language: language })) as string;
    // a new workshop starts in the trial (Profi in its first week) (read back where the second step allows it)
    const plan = (await this.effectivePlans([id])).get(id) ?? "basic";
    return { id, name, language, currency: "HUF", requiredFields: {}, general: {}, permissions: {}, plan, features: [] };
  }
  private async patchSettings(workshopId: string, key: string, value: unknown) {
    const current = check(await this.sb.from("workshops").select("settings").eq("id", workshopId).maybeSingle()) as DbRow | null;
    const settings = { ...((current?.settings as Record<string, unknown> | null) ?? {}), [key]: value };
    const rows = check(await this.sb.from("workshops").update({ settings }).eq("id", workshopId).select("id")) as DbRow[];
    if (rows.length === 0) throw new DomainError("NOT_ALLOWED");
  }
  async saveRequiredFields(workshopId: string, choices: RequiredFieldChoices) {
    await this.patchSettings(workshopId, "requiredFields", cleanRequiredChoices(choices));
  }
  async saveGeneralSettings(workshopId: string, values: Record<string, number | string | boolean | null>) {
    await this.patchSettings(workshopId, "general", values);
  }
  async savePermissions(workshopId: string, permissions: Record<string, Role[]>) {
    await this.patchSettings(workshopId, "permissions", permissions);
  }
  async saveFeatures(workshopId: string, features: string[]) {
    await this.patchSettings(workshopId, "features", [...new Set(features)]);
  }

  async searchCustomers(workshopId: string, query: string, limit = 100) {
    let req = this.sb.from("customers").select("*").eq("workshop_id", workshopId).order("name").limit(limit);
    const q = safe(query);
    if (q) {
      const parts = ["name", "phone", "mobile", "email", "city"].map((c) => `${c}.ilike.*${q}*`);
      // the owners of matching plates too (TST-001 finds its customer, #121)
      const key = plateSearchKey(q);
      if (key.length >= 3) {
        const v = check(await this.sb.from("vehicles").select("customer_id").eq("workshop_id", workshopId).ilike("plate_search", `%${key}%`).not("customer_id", "is", null).limit(50)) as DbRow[];
        const ids = [...new Set(v.map((r) => r.customer_id as string))];
        if (ids.length > 0) parts.push(`id.in.(${ids.join(",")})`);
      }
      req = req.or(parts.join(","));
    }
    return (check(await req) as DbRow[]).map(rowToCustomer);
  }
  async getCustomer(id: string) {
    const row = check(await this.sb.from("customers").select("*").eq("id", id).maybeSingle()) as DbRow | null;
    return row ? rowToCustomer(row) : null;
  }
  async saveCustomer(workshopId: string, input: CustomerInput, id?: string) {
    const row = customerToRow(workshopId, input);
    const res = id
      ? await this.sb.from("customers").update(row).eq("id", id).select("*").single()
      : await this.sb.from("customers").insert(row).select("*").single();
    return rowToCustomer(check(res) as DbRow);
  }
  async deleteCustomer(id: string) {
    checkDelete(await this.sb.from("customers").delete().eq("id", id), "CUSTOMER_HAS_WORK_ORDERS");
  }

  async searchVehicles(workshopId: string, query: string, limit = 100) {
    let req = this.sb.from("vehicles").select(VEHICLE_SELECT).eq("workshop_id", workshopId).order("plate_number").limit(limit);
    const q = safe(query);
    if (q) {
      const parts = ["make", "model", "vin", "engine_code"].map((c) => `${c}.ilike.*${q}*`);
      const key = plateSearchKey(q);
      if (key) parts.unshift(`plate_search.ilike.*${key}*`);
      req = req.or(parts.join(","));
    }
    return (check(await req) as DbRow[]).map(rowToVehicle);
  }
  async getVehicle(id: string) {
    const row = check(await this.sb.from("vehicles").select(VEHICLE_SELECT).eq("id", id).maybeSingle()) as DbRow | null;
    return row ? rowToVehicle(row) : null;
  }
  async vehiclesOfCustomer(customerId: string) {
    const rows = check(await this.sb.from("vehicles").select(VEHICLE_SELECT).eq("customer_id", customerId).order("plate_number"));
    return (rows as DbRow[]).map(rowToVehicle);
  }
  async saveVehicle(workshopId: string, input: VehicleInput, id?: string) {
    const row = vehicleToRow(workshopId, input);
    const res = id
      ? await this.sb.from("vehicles").update(row).eq("id", id).select(VEHICLE_SELECT).single()
      : await this.sb.from("vehicles").insert(row).select(VEHICLE_SELECT).single();
    return rowToVehicle(check(res) as DbRow);
  }
  async deleteVehicle(id: string) {
    checkDelete(await this.sb.from("vehicles").delete().eq("id", id), "VEHICLE_HAS_WORK_ORDERS");
  }

  /** Repeatable: rows are matched by their legacy id, so running it again updates instead of duplicating. */
  async importLegacy(workshopId: string, plan: ImportPlan, onProgress?: (p: ImportProgress) => void): Promise<ImportResult> {
    const legacyToId = new Map<string, string>();
    for (let i = 0; i < plan.customers.length; i += BATCH) {
      const rows = plan.customers.slice(i, i + BATCH).map((c) => customerToRow(workshopId, c));
      const saved = check(
        await this.sb.from("customers").upsert(rows, { onConflict: "workshop_id,legacy_id" }).select("id, legacy_id"),
      ) as DbRow[];
      for (const r of saved) legacyToId.set(r.legacy_id as string, r.id as string);
      onProgress?.({ phase: "customers", done: Math.min(i + BATCH, plan.customers.length), total: plan.customers.length });
    }
    // Owners imported earlier (not in this plan) are looked up too.
    const missing = [...new Set(plan.vehicles.map((v) => v.customerLegacyId).filter((x): x is string => !!x && !legacyToId.has(x)))];
    for (let i = 0; i < missing.length; i += BATCH) {
      const rows = check(
        await this.sb.from("customers").select("id, legacy_id").eq("workshop_id", workshopId).in("legacy_id", missing.slice(i, i + BATCH)),
      ) as DbRow[];
      for (const r of rows) legacyToId.set(r.legacy_id as string, r.id as string);
    }
    let withoutOwner = 0;
    for (let i = 0; i < plan.vehicles.length; i += BATCH) {
      const rows = plan.vehicles.slice(i, i + BATCH).map(({ customerLegacyId, ...v }) => {
        const customerId = customerLegacyId ? (legacyToId.get(customerLegacyId) ?? null) : null;
        if (!customerId) withoutOwner++;
        return vehicleToRow(workshopId, { ...v, customerId });
      });
      check(await this.sb.from("vehicles").upsert(rows, { onConflict: "workshop_id,legacy_id" }));
      onProgress?.({ phase: "vehicles", done: Math.min(i + BATCH, plan.vehicles.length), total: plan.vehicles.length });
    }
    onProgress?.({ phase: "done", done: 0, total: 0 });
    return { customers: plan.customers.length, vehicles: plan.vehicles.length, vehiclesWithoutOwner: withoutOwner };
  }

  // ---- work orders ----
  private async idsMatching(workshopId: string, q: string): Promise<{ vehicles: string[]; customers: string[] }> {
    const key = plateSearchKey(q);
    const [v, c] = await Promise.all([
      key ? this.sb.from("vehicles").select("id").eq("workshop_id", workshopId).ilike("plate_search", `%${key}%`).limit(200) : Promise.resolve({ data: [], error: null }),
      this.sb.from("customers").select("id").eq("workshop_id", workshopId).ilike("name", `%${q}%`).limit(200),
    ]);
    return { vehicles: (check(v) as DbRow[]).map((r) => r.id as string), customers: (check(c) as DbRow[]).map((r) => r.id as string) };
  }
  async searchWorkOrders(workshopId: string, query: string, filter: "active" | "all", limit = 100) {
    let req = this.sb.from("work_orders").select(WORK_ORDER_SELECT).eq("workshop_id", workshopId).order("received_at", { ascending: false }).order("number", { ascending: false }).limit(limit);
    if (filter === "active") req = req.neq("status", "closed");
    const q = safe(query);
    if (q) {
      const ids = await this.idsMatching(workshopId, q);
      const parts = [`number.ilike."*${q}*"`];
      if (ids.vehicles.length) parts.push(`vehicle_id.in.(${ids.vehicles.join(",")})`);
      if (ids.customers.length) parts.push(`customer_id.in.(${ids.customers.join(",")})`);
      // a part number used on the work order (owner's request, 2026-11-01)
      if (q.length >= 3) {
        const lines = check(await this.sb.from("work_order_items").select("work_order_id").eq("workshop_id", workshopId).ilike("part_number", `%${q}%`).limit(200)) as DbRow[];
        const woIds = [...new Set(lines.map((l) => l.work_order_id as string))].slice(0, 100);
        if (woIds.length) parts.push(`id.in.(${woIds.join(",")})`);
      }
      req = req.or(parts.join(","));
    }
    return (check(await req) as DbRow[]).map(rowToWorkOrder);
  }
  async workOrdersOfVehicle(vehicleId: string) {
    const rows = check(await this.sb.from("work_orders").select(WORK_ORDER_SELECT).eq("vehicle_id", vehicleId).order("received_at", { ascending: false }).limit(200));
    return (rows as DbRow[]).map(rowToWorkOrder);
  }
  async workOrdersOfCustomer(customerId: string) {
    const rows = check(await this.sb.from("work_orders").select(WORK_ORDER_SELECT).eq("customer_id", customerId).order("received_at", { ascending: false }).limit(200));
    return (rows as DbRow[]).map(rowToWorkOrder);
  }
  async getWorkOrder(id: string): Promise<WorkOrderDetail | null> {
    const row = check(await this.sb.from("work_orders").select(WORK_ORDER_SELECT).eq("id", id).maybeSingle()) as DbRow | null;
    if (!row) return null;
    const [items, events, quotes] = await Promise.all([
      this.sb.from("work_order_items").select("*").eq("work_order_id", id).order("sort_order").order("created_at"),
      this.sb.from("work_order_events").select("kind, reason, at").eq("work_order_id", id).order("id"),
      this.sb.from("quote_requests").select("*").eq("work_order_id", id).order("created_at", { ascending: false }),
    ]);
    return {
      order: rowToWorkOrder(row),
      items: (check(items) as DbRow[]).map(rowToItem),
      events: (check(events) as DbRow[]).map((e) => ({ kind: e.kind as WorkOrderEvent["kind"], reason: (e.reason as string) ?? null, at: e.at as string })),
      quotes: (check(quotes) as DbRow[]).map(rowToQuote),
    };
  }
  async createWorkOrder(workshopId: string, input: WorkOrderInput) {
    const row = check(await this.sb.from("work_orders").insert({ ...workOrderToRow(input), workshop_id: workshopId }).select(WORK_ORDER_SELECT).single()) as DbRow;
    return rowToWorkOrder(row);
  }
  async updateWorkOrder(id: string, input: WorkOrderInput, expectedUpdatedAt?: string) {
    let query = this.sb.from("work_orders").update(workOrderToRow(input)).eq("id", id);
    if (expectedUpdatedAt) query = query.eq("updated_at", expectedUpdatedAt);
    const rows = check(await query.select("updated_at")) as DbRow[];
    // Nothing updated although the row was readable: someone saved it in the meantime.
    if (expectedUpdatedAt && rows.length === 0) throw new DomainError("WORK_ORDER_CHANGED");
    return (rows[0]?.updated_at as string) ?? undefined;
  }
  async setWorkOrderStatus(id: string, status: WorkOrderStatus, payment?: PaymentMethod) {
    check(await this.sb.from("work_orders").update(payment && status === "closed" ? { status, payment_method: payment } : { status }).eq("id", id));
  }
  async markWorkOrderPaid(id: string, method: PaymentMethod | null, paid: boolean) {
    check(await this.sb.rpc("mark_work_order_paid", { p_id: id, p_method: method, p_paid: paid }));
  }
  async invoiceOf(workOrderId: string): Promise<Invoice | null> {
    const rows = check(await this.sb.rpc("invoice_state", { p_work_order_id: workOrderId })) as DbRow[];
    const r = rows[0];
    return r
      ? {
          status: r.status as Invoice["status"], number: (r.number as string) ?? null, gross: r.gross === null ? null : Number(r.gross), currency: r.currency as string,
          viewUrl: (r.view_url as string) ?? null, issuedAt: (r.issued_at as string) ?? null,
          cancelStatus: (r.cancel_status as Invoice["cancelStatus"]) ?? null, cancelReason: (r.cancel_reason as string) ?? null,
        }
      : null;
  }
  /** Calls an invoicing edge function; its { error, detail } answer becomes a DomainError. */
  private async invoiceFunction<T>(name: string, body: Record<string, unknown>, fallback: ErrorCode): Promise<T> {
    const { data, error } = await this.sb.functions.invoke(name, { body });
    if (error) {
      let code: string = fallback;
      let detail = "";
      try {
        const b = await (error as { context?: Response }).context?.json();
        if (b?.error) code = b.error;
        if (b?.detail) detail = b.detail;
      } catch {
        // no JSON body
      }
      throw new DomainError(code as ErrorCode, detail);
    }
    return data as T;
  }
  async taxpayerLookup(workshopId: string, taxNumber: string): Promise<TaxpayerData> {
    return (await this.invoiceFunction<{ taxpayer: TaxpayerData }>("company-lookup", { action: "taxNumber", workshopId, taxNumber }, "TAXPAYER_FAILED")).taxpayer;
  }
  async companyLookup(workshopId: string, name: string, city?: string | null) {
    return this.invoiceFunction<{ candidates: CompanyCandidate[]; keySet: boolean }>("company-lookup", { action: "byName", workshopId, name, city: city ?? null }, "AI_FAILED");
  }
  async issueInvoice(workOrderId: string): Promise<Invoice> {
    return (await this.invoiceFunction<{ invoice: Invoice }>("issue-invoice", { workOrderId }, "INVOICE_FAILED")).invoice;
  }
  async resolveInvoice(workOrderId: string, number: string | null) {
    check(await this.sb.rpc("resolve_invoice", { p_work_order_id: workOrderId, p_number: number }));
  }
  async cancelInvoice(workOrderId: string, reason: string) {
    const r = await this.invoiceFunction<{ cancel: { number: string } }>("cancel-invoice", { workOrderId, reason }, "INVOICE_CANCEL_FAILED");
    return { number: r.cancel.number };
  }
  async resolveInvoiceCancel(workOrderId: string, number: string | null) {
    check(await this.sb.rpc("resolve_invoice_cancel", { p_work_order_id: workOrderId, p_number: number }));
  }
  async cancelledInvoices(workOrderId: string): Promise<CancelledInvoice[]> {
    const rows = check(await this.sb.rpc("cancelled_invoices", { p_work_order_id: workOrderId })) as DbRow[];
    return rows.map((r) => ({
      number: (r.number as string) ?? null, gross: r.gross === null ? null : Number(r.gross), currency: r.currency as string, issuedAt: (r.issued_at as string) ?? null,
      cancelNumber: (r.cancel_number as string) ?? null, cancelledAt: (r.cancelled_at as string) ?? null, cancelReason: (r.cancel_reason as string) ?? null,
    }));
  }
  async invoiceKeySet(workshopId: string) {
    return check(await this.sb.rpc("invoice_key_set", { p_workshop_id: workshopId })) as boolean;
  }
  async setInvoiceKey(workshopId: string, key: string) {
    check(await this.sb.rpc("set_invoice_key", { p_workshop_id: workshopId, p_key: key }));
  }
  async reopenWorkOrder(id: string, reason: string) {
    check(await this.sb.rpc("reopen_work_order", { p_id: id, p_reason: reason }));
  }
  async deleteWorkOrder(id: string) {
    const rows = check(await this.sb.from("work_orders").delete().eq("id", id).select("id")) as DbRow[];
    if (rows.length === 0) throw new DomainError("NOT_ALLOWED");
  }
  async saveWorkOrderItem(workshopId: string, workOrderId: string, line: WorkOrderLineInput, id?: string) {
    const b = buildLine(line);
    const row: DbRow = {
      kind: b.kind, description: b.description, part_number: b.partNumber, article_id: b.articleId ?? null, unit: b.unit, quantity: b.quantity,
      unit_price: b.unitPrice, price_mode: b.priceMode, vat_rate: b.vatRate, net: b.net, vat: b.vat, gross: b.gross,
      approval: b.approval, optional: b.optional, customer_supplied: b.customerSupplied,
    };
    if (id) return rowToItem(check(await this.sb.from("work_order_items").update(row).eq("id", id).select("*").single()) as DbRow);
    const count = check(await this.sb.from("work_order_items").select("id").eq("work_order_id", workOrderId)) as DbRow[];
    const saved = await this.sb
      .from("work_order_items")
      .insert({ ...row, workshop_id: workshopId, work_order_id: workOrderId, sort_order: count.length + 1 })
      .select("*")
      .single();
    return rowToItem(check(saved) as DbRow);
  }
  async createQuoteRequest(workOrderId: string) {
    return check(await this.sb.rpc("create_quote_request", { p_work_order: workOrderId })) as string;
  }
  async recordQuoteAnswer(workOrderId: string, approvedIds: string[], note?: string) {
    check(await this.sb.rpc("record_quote_answer", { p_work_order: workOrderId, p_approved: approvedIds, p_note: note ?? null }));
  }
  async quoteView(token: string) {
    return check(await this.sb.rpc("quote_view", { p_token: token })) as PublicQuote;
  }
  async quoteAnswer(token: string, approvedIds: string[]) {
    check(await this.sb.rpc("quote_answer", { p_token: token, p_approved: approvedIds }));
  }
  async recentQuoteAnswers(workshopId: string, sinceIso: string): Promise<QuoteNotice[]> {
    const rows = check(
      await this.sb.from("quote_requests").select("id, work_order_id, answered_at, item_ids, approved_ids").eq("workshop_id", workshopId)
        .eq("channel", "link").gt("answered_at", sinceIso).order("answered_at", { ascending: false }).limit(20),
    ) as DbRow[];
    if (rows.length === 0) return [];
    const orders = (check(await this.sb.from("work_orders").select(WORK_ORDER_SELECT).in("id", [...new Set(rows.map((r) => r.work_order_id as string))])) as DbRow[]).map(rowToWorkOrder);
    return rows.flatMap((r) => {
      const o = orders.find((x) => x.id === r.work_order_id);
      return o
        ? [{ quoteId: r.id as string, workOrderId: o.id, number: o.number, plateNumber: o.plateNumber ?? null, plateCountry: o.plateCountry ?? null, customerName: o.customerName ?? null,
             answeredAt: r.answered_at as string, approved: ((r.approved_ids as string[]) ?? []).length, total: ((r.item_ids as string[]) ?? []).length }]
        : [];
    });
  }
  async deleteWorkOrderItem(id: string) {
    check(await this.sb.from("work_order_items").delete().eq("id", id));
  }
  async itemSuggestions(workshopId: string, text: string, limit = 8): Promise<ItemSuggestion[]> {
    const q = safe(text);
    if (!q) return [];
    const rows = check(
      await this.sb.from("work_order_items").select("kind, description, part_number, unit, quantity, unit_price, price_mode, vat_rate")
        .eq("workshop_id", workshopId).or(`description.ilike.*${q}*,part_number.ilike.*${q}*`).order("created_at", { ascending: false }).limit(60),
    ) as DbRow[];
    const seen = new Set<string>();
    const out: ItemSuggestion[] = [];
    for (const r of rows) {
      const k = (r.description as string).toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ kind: r.kind as ItemSuggestion["kind"], description: r.description as string, partNumber: (r.part_number as string) ?? null, unit: (r.unit as string) ?? null,
        quantity: Number(r.quantity), unitPrice: Number(r.unit_price), priceMode: r.price_mode as ItemSuggestion["priceMode"], vatRate: Number(r.vat_rate) });
      if (out.length >= limit) break;
    }
    return out;
  }
  async searchArticles(workshopId: string, query: string, limit = 50): Promise<Article[]> {
    let req = this.sb.from("articles").select("*").eq("workshop_id", workshopId).eq("active", true)
      .order("usage_count", { ascending: false }).order("name").limit(limit);
    const q = safe(query);
    if (q) {
      const key = partSearchKey(q);
      const parts = [`name.ilike.*${q}*`];
      if (key) {
        parts.unshift(`part_search.ilike.${key}*`);
        const cross = check(await this.sb.from("article_numbers").select("article_id").eq("workshop_id", workshopId).ilike("number_search", `${key}%`).limit(50)) as DbRow[];
        if (cross.length) parts.push(`id.in.(${[...new Set(cross.map((c) => c.article_id as string))].join(",")})`);
      }
      req = req.or(parts.join(","));
    }
    return (check(await req) as DbRow[]).map(rowToArticle);
  }
  async getArticle(id: string) {
    const row = check(await this.sb.from("articles").select("*, article_numbers(id, number, kind, brand)").eq("id", id).maybeSingle()) as DbRow | null;
    return row ? rowToArticle(row) : null;
  }
  async saveArticle(workshopId: string, input: ArticleInput, id?: string) {
    const a = validateArticle(input);
    const row = articleToRow(a);
    const saved = id
      ? check(await this.sb.from("articles").update(row).eq("id", id).select("*, article_numbers(id, number, kind, brand)").single())
      : check(await this.sb.from("articles").insert({ ...row, workshop_id: workshopId }).select("*, article_numbers(id, number, kind, brand)").single());
    return rowToArticle(saved as DbRow);
  }
  async deleteArticle(id: string) {
    const res = await this.sb.from("articles").delete().eq("id", id).select("id");
    if (res.error?.code === "23503") throw new DomainError("ARTICLE_IN_USE");
    if ((check(res) as DbRow[]).length === 0) throw new DomainError("NOT_ALLOWED");
  }
  async addArticleNumber(workshopId: string, articleId: string, n: ArticleNumber) {
    const r = check(await this.sb.from("article_numbers").insert({ workshop_id: workshopId, article_id: articleId, number: n.number.trim().toUpperCase(), kind: n.kind, brand: n.brand ?? null }).select("*").single()) as DbRow;
    return { id: r.id as string, number: r.number as string, kind: r.kind as ArticleNumberRow["kind"], brand: (r.brand as string) ?? null };
  }
  async deleteArticleNumber(id: string) {
    check(await this.sb.from("article_numbers").delete().eq("id", id));
  }
  async stockMovements(articleId: string, limit = 100): Promise<StockMovement[]> {
    const rows = check(await this.sb.from("stock_movements").select("*, work_order_items(work_orders(number))").eq("article_id", articleId).order("created_at", { ascending: false }).limit(limit)) as DbRow[];
    return rows.map((r) => ({
      id: String(r.id),
      articleId: r.article_id as string,
      quantity: Number(r.quantity),
      kind: r.kind as StockMovement["kind"],
      unitCost: r.unit_cost === null ? null : Number(r.unit_cost),
      supplier: (r.supplier as string) ?? null,
      documentNo: (r.document_no as string) ?? null,
      note: (r.note as string) ?? null,
      workOrderNumber: (((r.work_order_items as DbRow | null)?.work_orders as DbRow | null)?.number as string) ?? null,
      createdAt: r.created_at as string,
    }));
  }
  async addStockMovement(workshopId: string, m: NewStockMovement) {
    if (!Number.isFinite(m.quantity) || m.quantity === 0) throw new DomainError("INVALID_STOCK_QUANTITY");
    check(await this.sb.from("stock_movements").insert({ workshop_id: workshopId, article_id: m.articleId, quantity: m.quantity, kind: m.kind, unit_cost: m.unitCost ?? null,
      supplier: m.supplier?.trim() || null, document_no: m.documentNo?.trim() || null, note: m.note?.trim() || null }));
  }
  async importLegacyArticles(workshopId: string, articles: LegacyArticle[], onProgress?: (p: ImportProgress) => void) {
    let n = 0;
    const CHUNK = 1000;
    for (let i = 0; i < articles.length; i += CHUNK) {
      n += check(await this.sb.rpc("import_legacy_articles", { p_workshop_id: workshopId, p_rows: articles.slice(i, i + CHUNK) })) as number;
      onProgress?.({ phase: "articles", done: Math.min(i + CHUNK, articles.length), total: articles.length });
    }
    onProgress?.({ phase: "done", done: 0, total: 0 });
    return n;
  }
  async supplementLegacyWorkOrders(workshopId: string, rows: LegacyWorkOrderExtra[], onProgress?: (p: ImportProgress) => void) {
    let n = 0;
    const CHUNK = 1000;
    for (let i = 0; i < rows.length; i += CHUNK) {
      n += check(await this.sb.rpc("supplement_legacy_work_orders", { p_workshop_id: workshopId, p_rows: rows.slice(i, i + CHUNK) })) as number;
      onProgress?.({ phase: "extras", done: Math.min(i + CHUNK, rows.length), total: rows.length });
    }
    onProgress?.({ phase: "done", done: 0, total: 0 });
    return n;
  }
  async termsAcceptances(workshopId: string) {
    const rows = check(await this.sb.from("terms_acceptances").select("document, version, accepted_at").eq("workshop_id", workshopId)) as DbRow[];
    return rows.map((r) => ({ document: r.document as string, version: r.version as string, acceptedAt: r.accepted_at as string }));
  }
  async acceptTerms(workshopId: string, version: string, details?: ContractDetails): Promise<void> {
    if (!details) throw new DomainError("REQUIRED_MISSING");
    check(await this.sb.rpc("accept_terms", {
      p_workshop_id: workshopId, p_version: version, p_company: details.companyName, p_address: details.address,
      p_tax_number: details.taxNumber, p_reg_number: details.regNumber ?? null,
    }));
    // The confirmation e-mail must not block the work; the operator sees whether it went out.
    const { error } = await this.sb.functions.invoke("contract-confirmation", { body: { workshopId } });
    if (error) console.warn("contract confirmation", error.message);
  }
  async opContracts(workshopId: string): Promise<OpContract[]> {
    const rows = check(await this.sb.rpc("op_contracts", { p_workshop_id: workshopId })) as DbRow[];
    return rows.map((r) => ({
      version: r.version as string, companyName: r.company_name as string, address: r.address as string, taxNumber: r.tax_number as string,
      regNumber: (r.reg_number as string) ?? null, userEmail: (r.user_email as string) ?? null, acceptedAt: r.accepted_at as string,
      confirmationSentAt: (r.confirmation_sent_at as string) ?? null,
    }));
  }
  async opExtendTrial(workshopId: string, days: number): Promise<string> {
    return String(check(await this.sb.rpc("op_extend_trial", { p_workshop_id: workshopId, p_days: days }))).slice(0, 10);
  }
  async opTrialActivity(workshopId: string): Promise<OpTrialActivity> {
    const r = (check(await this.sb.rpc("op_trial_activity", { p_workshop_id: workshopId })) ?? {}) as DbRow;
    return {
      workOrders: Number(r.workOrders ?? 0), vehicles: Number(r.vehicles ?? 0), signedIn: Number(r.signedIn ?? 0),
      lastActivity: (r.lastActivity as string) ?? null, lastSignIn: (r.lastSignIn as string) ?? null,
    };
  }
  async opCustomRequests(workshopId: string): Promise<OpCustomRequest[]> {
    const rows = check(await this.sb.rpc("op_custom_requests", { p_workshop_id: workshopId })) as DbRow[];
    return rows.map((r) => ({ ...customRequestOf(r), createdByEmail: (r.created_by_email as string) ?? null }));
  }
  async opAddCustomRequest(workshopId: string, r: NewCustomRequest) {
    check(await this.sb.rpc("op_add_custom_request", { p_workshop_id: workshopId, p_year: r.year, p_title: r.title, p_hours: r.hours, p_included: r.included }));
  }
  async opUpdateCustomRequest(id: string, status: CustomRequestStatus, hours: number | null = null) {
    check(await this.sb.rpc("op_update_custom_request", { p_id: id, p_status: status, p_hours: hours }));
  }
  async opStatsOverview(from: string, to: string): Promise<OpStatsOverview> {
    const rows = check(await this.sb.rpc("op_stats_overview", { p_from: from, p_to: to })) as DbRow[];
    const totals = (period: string): OpStatTotals => {
      const r = rows.find((x) => x.period === period) ?? {};
      return { ...nums(r, OP_STAT_FIGURES), ...nums(r, OP_STAT_PLATFORM), from: String(r.date_from ?? ""), to: String(r.date_to ?? "") };
    };
    return { current: totals("current"), previous: totals("previous") };
  }
  async opStatsWorkshops(from: string, to: string): Promise<OpStatsWorkshop[]> {
    const rows = check(await this.sb.rpc("op_stats_workshops", { p_from: from, p_to: to })) as DbRow[];
    return rows.map((r) => ({
      ...(nums(r, OP_STAT_FIGURES) as OpStatFigures),
      workshopId: r.workshop_id as string,
      name: r.name as string,
      plan: r.plan as Plan,
      currency: (r.currency as string) ?? "HUF",
      createdAt: r.created_at as string,
      ownerEmail: (r.owner_email as string) ?? null,
      billingStatus: (r.billing_status as BillingStatus) ?? "trial",
      monthlyFee: r.monthly_fee === null || r.monthly_fee === undefined ? null : Number(r.monthly_fee),
      seatsUsed: Number(r.seats_used ?? 0),
      seatLimit: Number(r.seat_limit ?? 100000),
      lastActivity: (r.last_activity as string) ?? null,
    }));
  }
  async opStatsMonthly(months: number, workshopId?: string | null): Promise<OpStatsMonth[]> {
    const rows = check(await this.sb.rpc("op_stats_monthly", { p_months: months, p_workshop_id: workshopId ?? null })) as DbRow[];
    return rows.map((r) => ({ ...nums(r, OP_STAT_MONTH), month: String(r.month) }));
  }
  async exportAll(workshopId: string): Promise<ExportBundle> {
    const tables: ExportBundle["tables"] = {};
    tables.workshops = check(await this.sb.from("workshops").select("*").eq("id", workshopId)) as DbRow[];
    for (const name of EXPORT_TABLES) {
      const rows: DbRow[] = [];
      for (let from = 0; ; from += 1000) {
        const page = check(await this.sb.from(name).select("*").eq("workshop_id", workshopId).order(EXPORT_ORDER[name] ?? "id").range(from, from + 999)) as DbRow[];
        rows.push(...page);
        if (page.length < 1000) break;
      }
      tables[name] = rows;
    }
    const photos = (tables.photos ?? []).map((r) => {
      const path = r.storage_path as string;
      return {
        name: `photos/${path.split("/").pop()}`,
        load: async () => check(await this.sb.storage.from(PHOTO_BUCKET).download(path)) as Blob,
      };
    });
    // the phone signatures with them (signatures/<id>.png, see work_order_signatures.csv)
    for (const r of tables.work_order_signatures ?? []) {
      const path = r.storage_path as string;
      photos.push({ name: `signatures/${path.split("/").pop()}`, load: async () => check(await this.sb.storage.from(SIGNATURE_BUCKET).download(path)) as Blob });
    }
    return { tables, photos };
  }

  async listPhotos(filter: { workOrderId?: string; vehicleId?: string }): Promise<Photo[]> {
    let req = this.sb.from("photos").select("*").order("created_at", { ascending: false }).limit(200);
    if (filter.workOrderId) req = req.eq("work_order_id", filter.workOrderId);
    if (filter.vehicleId) req = req.eq("vehicle_id", filter.vehicleId);
    const rows = check(await req) as DbRow[];
    if (rows.length === 0) return [];
    const signed = check(await this.sb.storage.from(PHOTO_BUCKET).createSignedUrls(rows.map((r) => r.storage_path as string), 3600)) as { path: string | null; signedUrl: string }[];
    const urls = new Map(signed.map((s) => [s.path, s.signedUrl]));
    return rows.map((r) => rowToPhoto(r, urls.get(r.storage_path as string) ?? ""));
  }
  async addPhoto(workshopId: string, photo: NewPhoto, image: { blob: Blob; width: number; height: number }): Promise<Photo> {
    const id = crypto.randomUUID();
    const path = `${workshopId}/${id}.jpg`;
    check(await this.sb.storage.from(PHOTO_BUCKET).upload(path, image.blob, { contentType: "image/jpeg", upsert: false }));
    const res = await this.sb
      .from("photos")
      .insert({ id, workshop_id: workshopId, vehicle_id: photo.vehicleId, work_order_id: photo.workOrderId, kind: photo.kind, caption: photo.caption ?? null,
        ...(photo.kind === "intake" ? { intake_view: photo.intakeView ?? null } : {}),
        storage_path: path, width: image.width, height: image.height, bytes: image.blob.size })
      .select("*")
      .single();
    if (res.error) {
      // Do not leave an image without its record behind.
      await this.sb.storage.from(PHOTO_BUCKET).remove([path]);
      check(res);
    }
    const signed = await this.sb.storage.from(PHOTO_BUCKET).createSignedUrl(path, 3600);
    return rowToPhoto(res.data as DbRow, signed.data?.signedUrl ?? "");
  }
  async deletePhoto(id: string) {
    const rows = check(await this.sb.from("photos").delete().eq("id", id).select("storage_path")) as DbRow[];
    if (rows.length === 0) throw new DomainError("NOT_ALLOWED");
    await this.sb.storage.from(PHOTO_BUCKET).remove(rows.map((r) => r.storage_path as string));
  }

  private signatureUrls = new Map<string, Promise<string>>();
  /** The signature image as an object URL (downloaded once, does not expire, so a long open work order prints with it). */
  private signatureImage(path: string): Promise<string> {
    let url = this.signatureUrls.get(path);
    if (!url) {
      url = (async () => {
        const { data, error } = await this.sb.storage.from(SIGNATURE_BUCKET).download(path);
        return error || !data ? "" : URL.createObjectURL(data);
      })().catch(() => "");
      this.signatureUrls.set(path, url);
    }
    return url;
  }
  private async rowToSignature(r: DbRow): Promise<WorkOrderSignature> {
    return {
      id: r.id as string, workOrderId: r.work_order_id as string, kind: r.kind as SignatureKind, signerName: r.signer_name as string,
      signedAt: r.signed_at as string, contentHash: r.content_hash as string, content: r.content as SignedContent,
      imageUrl: await this.signatureImage(r.storage_path as string),
    };
  }
  async signatures(workOrderId: string): Promise<WorkOrderSignature[]> {
    const rows = check(await this.sb.from("work_order_signatures").select("*").eq("work_order_id", workOrderId).order("signed_at")) as DbRow[];
    return Promise.all(rows.map((r) => this.rowToSignature(r)));
  }
  async addSignature(workshopId: string, workOrderId: string, s: NewSignature): Promise<WorkOrderSignature> {
    const id = crypto.randomUUID();
    const path = `${workshopId}/${id}.png`;
    // the image first (the bucket never lets a user change or delete it), then the record that points to it
    check(await this.sb.storage.from(SIGNATURE_BUCKET).upload(path, s.image, { contentType: "image/png", upsert: false }));
    const row = check(
      await this.sb.from("work_order_signatures").insert({
        id, workshop_id: workshopId, work_order_id: workOrderId, kind: s.kind, signer_name: s.signerName.trim(), storage_path: path,
        content: s.content, content_hash: s.contentHash, user_agent: s.userAgent?.slice(0, 500) ?? null,
      }).select("*").single(),
    ) as DbRow;
    this.signatureUrls.set(path, Promise.resolve(URL.createObjectURL(s.image)));
    return this.rowToSignature(row);
  }
  async createIntakeLink(workOrderId: string) {
    return check(await this.sb.rpc("create_intake_link", { p_work_order: workOrderId })) as string;
  }
  async lastIntakeLink(workOrderId: string): Promise<IntakeLink | null> {
    const r = check(await this.sb.from("intake_links").select("token, created_at, expires_at, sent_to, sent_at").eq("work_order_id", workOrderId)
      .order("created_at", { ascending: false }).limit(1).maybeSingle()) as DbRow | null;
    return r ? { token: r.token as string, createdAt: r.created_at as string, expiresAt: r.expires_at as string, sentTo: (r.sent_to as string) ?? null, sentAt: (r.sent_at as string) ?? null } : null;
  }
  async sendIntakeEmail(workOrderId: string, message: QuoteMessage) {
    return this.invokeSend({ workOrderId, channel: "intakeEmail", ...message });
  }
  async intakeView(token: string): Promise<PublicIntake> {
    const { data, error } = await this.sb.functions.invoke("intake-view", { body: { token } });
    if (error) {
      let code = "INTAKE_NOT_FOUND";
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) code = body.error;
      } catch {
        // no JSON body
      }
      throw toError(code);
    }
    return data as PublicIntake;
  }
  async intakeObject(token: string, text: string) {
    check(await this.sb.rpc("intake_object", { p_token: token, p_text: text }));
  }
  private rowToObjection = (r: DbRow): IntakeObjection => ({
    id: r.id as string, workOrderId: r.work_order_id as string, text: r.text as string, createdAt: r.created_at as string, resolvedAt: (r.resolved_at as string) ?? null,
  });
  async intakeObjections(workOrderId: string): Promise<IntakeObjection[]> {
    const rows = check(await this.sb.from("intake_objections").select("id, work_order_id, text, created_at, resolved_at").eq("work_order_id", workOrderId).order("created_at", { ascending: false })) as DbRow[];
    return rows.map(this.rowToObjection);
  }
  async recentIntakeObjections(workshopId: string, sinceIso: string): Promise<IntakeObjectionNotice[]> {
    const rows = check(await this.sb.from("intake_objections").select("id, work_order_id, text, created_at, resolved_at").eq("workshop_id", workshopId)
      .gt("created_at", sinceIso).order("created_at", { ascending: false }).limit(20)) as DbRow[];
    if (rows.length === 0) return [];
    const orders = (check(await this.sb.from("work_orders").select(WORK_ORDER_SELECT).in("id", [...new Set(rows.map((r) => r.work_order_id as string))])) as DbRow[]).map(rowToWorkOrder);
    return rows.flatMap((r) => {
      const o = orders.find((x) => x.id === r.work_order_id);
      return o ? [{ ...this.rowToObjection(r), number: o.number, plateNumber: o.plateNumber ?? null, plateCountry: o.plateCountry ?? null, customerName: o.customerName ?? null }] : [];
    });
  }
  async resolveIntakeObjection(id: string) {
    check(await this.sb.rpc("resolve_intake_objection", { p_id: id }));
  }

  /** Removes the workshop's logo files except `keep` (older versions, a failed earlier upload). */
  private async tidyLogos(workshopId: string, keep: string | null) {
    const { data } = await this.sb.storage.from(LOGO_BUCKET).list(workshopId);
    const old = (data ?? []).map((f) => `${workshopId}/${f.name}`).filter((p) => p !== keep);
    if (old.length > 0) await this.sb.storage.from(LOGO_BUCKET).remove(old);
  }
  async saveLogo(workshopId: string, image: PreparedLogo): Promise<WorkshopLogo> {
    const version = newLogoVersion();
    const path = logoPath(workshopId, version, image.type);
    const up = await this.sb.storage.from(LOGO_BUCKET).upload(path, image.blob, { contentType: image.type, upsert: false, cacheControl: "31536000" });
    if (up.error) throw toError(up.error.message);
    const logo: WorkshopLogo = { path, version, width: image.width, height: image.height };
    try {
      await this.patchSettings(workshopId, "logo", logo);
    } catch (e) {
      await this.sb.storage.from(LOGO_BUCKET).remove([path]);
      throw e;
    }
    this.logoUrls.set(path, Promise.resolve(URL.createObjectURL(image.blob)));
    await this.tidyLogos(workshopId, path).catch(() => undefined);
    return logo;
  }
  async removeLogo(workshopId: string) {
    await this.patchSettings(workshopId, "logo", null);
    await this.tidyLogos(workshopId, null).catch(() => undefined);
  }
  private logoUrls = new Map<string, Promise<string | null>>();
  logoImageUrl(_workshopId: string, logo: WorkshopLogo): Promise<string | null> {
    // a downloaded copy (object URL): it never expires, so a long open work order still prints with it
    let url = this.logoUrls.get(logo.path);
    if (!url) {
      url = (async () => {
        const { data, error } = await this.sb.storage.from(LOGO_BUCKET).download(logo.path);
        return error || !data ? null : URL.createObjectURL(data);
      })().catch(() => null);
      this.logoUrls.set(logo.path, url);
    }
    return url;
  }
  async quoteLogoUrl(token: string): Promise<string | null> {
    // the logo is a nicety: the quote page works the same without it
    try {
      const { data, error } = await this.sb.functions.invoke("quote-logo", { body: { token } });
      const url = (data as { url?: unknown } | null)?.url;
      return error || typeof url !== "string" ? null : url;
    } catch {
      return null;
    }
  }

  async feedbackReport(workshopId: string, input: FeedbackInput) {
    return this.invoiceFunction<{ issueNumber: number }>("feedback", { action: "report", workshopId, ...input, screenshot: input.screenshot ?? null }, "FEEDBACK_FAILED");
  }
  async feedbackList(workshopId: string) {
    return (await this.invoiceFunction<{ reports: FeedbackReport[] }>("feedback", { action: "list", workshopId }, "FEEDBACK_FAILED")).reports;
  }
  async feedbackReply(workshopId: string, issueNumber: number, verdict: FeedbackVerdict, text: string) {
    await this.invoiceFunction("feedback", { action: "reply", workshopId, issueNumber, verdict, text }, "FEEDBACK_FAILED");
  }

  async lastOdometer(vehicleId: string) {
    const [o, v] = await Promise.all([
      this.sb.from("work_orders").select("odometer_km").eq("vehicle_id", vehicleId).not("odometer_km", "is", null).order("odometer_km", { ascending: false }).limit(1),
      this.sb.from("vehicles").select("odometer_km").eq("id", vehicleId).maybeSingle(),
    ]);
    const a = ((check(o) as DbRow[])[0]?.odometer_km as number | undefined) ?? -1;
    const b = ((check(v) as DbRow | null)?.odometer_km as number | null | undefined) ?? -1;
    const max = Math.max(a, b);
    return max >= 0 ? max : null;
  }

  private async all(table: string, columns: string, workshopId: string): Promise<DbRow[]> {
    const out: DbRow[] = [];
    for (let from = 0; ; from += PAGE) {
      const rows = check(await this.sb.from(table).select(columns).eq("workshop_id", workshopId).order("id").range(from, from + PAGE - 1)) as unknown as DbRow[];
      out.push(...rows);
      if (rows.length < PAGE) return out;
    }
  }
  async importLegacyWorkOrders(workshopId: string, orderRows: Row[], itemRows: Row[], onProgress?: (p: ImportProgress) => void): Promise<WorkOrderImportResult> {
    const [vehicles, customers] = await Promise.all([
      this.all("vehicles", "id, legacy_id, plate_number, customer_id", workshopId),
      this.all("customers", "id, legacy_id", workshopId),
    ]);
    if (vehicles.length === 0 || customers.length === 0) throw new DomainError("IMPORT_NEEDS_MASTER_DATA");
    const byPlate = new Map<string, { id: string; customerId: string | null }[]>();
    for (const v of vehicles) {
      const k = plateSearchKey(v.plate_number as string);
      byPlate.set(k, [...(byPlate.get(k) ?? []), { id: v.id as string, customerId: (v.customer_id as string) ?? null }]);
    }
    const plan = planLegacyWorkOrders(orderRows, itemRows, {
      vehicleByLegacyId: new Map(vehicles.filter((v) => v.legacy_id).map((v) => [v.legacy_id as string, v.id as string])),
      vehiclesByPlate: byPlate,
      customerByLegacyId: new Map(customers.filter((c) => c.legacy_id).map((c) => [c.legacy_id as string, c.id as string])),
      ownerOfVehicle: new Map(vehicles.map((v) => [v.id as string, (v.customer_id as string) ?? null])),
    });
    const itemsByOrder = new Map<string, typeof plan.items>();
    for (const i of plan.items) itemsByOrder.set(i.workOrderLegacyId, [...(itemsByOrder.get(i.workOrderLegacyId) ?? []), i]);
    let created = 0;
    const CHUNK = 300;
    for (let i = 0; i < plan.orders.length; i += CHUNK) {
      const orders = plan.orders.slice(i, i + CHUNK);
      const items = orders.flatMap((o) => itemsByOrder.get(o.legacyId) ?? []);
      created += check(await this.sb.rpc("import_legacy_work_orders", { p_workshop_id: workshopId, p_orders: orders, p_items: items })) as number;
      onProgress?.({ phase: "workOrders", done: Math.min(i + CHUNK, plan.orders.length), total: plan.orders.length });
    }
    onProgress?.({ phase: "done", done: 0, total: 0 });
    return { workOrders: created, items: plan.items.length, skippedOrders: plan.skippedOrders, skippedItems: plan.skippedItems, toReview: plan.toReview };
  }
}

/** A custom_requests row (snake case, numeric hours as text) as the app uses it. */
function customRequestOf(r: DbRow): CustomRequest {
  const status = r.status === "done" || r.status === "cancelled" ? r.status : "open";
  return { id: String(r.id), year: Number(r.year), title: String(r.title ?? ""), hours: Number(r.hours ?? 0), included: r.included === true, status, createdAt: String(r.created_at ?? "") };
}
